import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import logoService from '../../../services/logoService';

const DEFAULT_ICON = '/assets/images/crc_logo_dia_logo_500-1759554868300.png';
const DEFAULT_TEXT = '/assets/images/crc_logo_dia_text_500-1759554658424.png';

const LogoBrandingManagement = () => {
  const { userProfile } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [iconUploading, setIconUploading] = useState(false);
  const [textUploading, setTextUploading] = useState(false);
  const [iconPreview, setIconPreview] = useState(null);
  const [textPreview, setTextPreview] = useState(null);
  const [message, setMessage] = useState(null);

  const iconInputRef = useRef(null);
  const textInputRef = useRef(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    const { data } = await logoService?.getLogoSettings();
    setSettings(data);
    setLoading(false);
  };

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 4000);
  };

  const handleIconUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    // Preview
    const reader = new FileReader();
    reader.onload = (ev) => setIconPreview(ev?.target?.result);
    reader?.readAsDataURL(file);

    setIconUploading(true);
    try {
      // Delete old file if exists
      if (settings?.logo_icon_path) {
        await logoService?.deleteLogoFile(settings?.logo_icon_path);
      }

      const { data: uploadData, error: uploadError } = await logoService?.uploadLogoIcon(file);
      if (uploadError) throw uploadError;

      const { error: saveError } = await logoService?.saveLogoSettings({
        logoIconUrl: uploadData?.url,
        logoIconPath: uploadData?.path,
        updatedBy: userProfile?.id,
      });
      if (saveError) throw saveError;

      await loadSettings();
      showMessage('Logo icon updated successfully!');
    } catch (err) {
      showMessage(err?.message || 'Failed to upload logo icon', 'error');
    } finally {
      setIconUploading(false);
      if (iconInputRef?.current) iconInputRef.current.value = '';
    }
  };

  const handleTextUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (ev) => setTextPreview(ev?.target?.result);
    reader?.readAsDataURL(file);

    setTextUploading(true);
    try {
      if (settings?.logo_text_path) {
        await logoService?.deleteLogoFile(settings?.logo_text_path);
      }

      const { data: uploadData, error: uploadError } = await logoService?.uploadLogoText(file);
      if (uploadError) throw uploadError;

      const { error: saveError } = await logoService?.saveLogoSettings({
        logoTextUrl: uploadData?.url,
        logoTextPath: uploadData?.path,
        updatedBy: userProfile?.id,
      });
      if (saveError) throw saveError;

      await loadSettings();
      showMessage('Logo text updated successfully!');
    } catch (err) {
      showMessage(err?.message || 'Failed to upload logo text', 'error');
    } finally {
      setTextUploading(false);
      if (textInputRef?.current) textInputRef.current.value = '';
    }
  };

  const handleResetIcon = async () => {
    if (!window.confirm('Reset logo icon to default?')) return;
    setIconUploading(true);
    try {
      if (settings?.logo_icon_path) {
        await logoService?.deleteLogoFile(settings?.logo_icon_path);
      }
      await logoService?.saveLogoSettings({
        logoIconUrl: null,
        logoIconPath: null,
        updatedBy: userProfile?.id,
      });
      setIconPreview(null);
      await loadSettings();
      showMessage('Logo icon reset to default.');
    } catch (err) {
      showMessage(err?.message || 'Reset failed', 'error');
    } finally {
      setIconUploading(false);
    }
  };

  const handleResetText = async () => {
    if (!window.confirm('Reset logo text to default?')) return;
    setTextUploading(true);
    try {
      if (settings?.logo_text_path) {
        await logoService?.deleteLogoFile(settings?.logo_text_path);
      }
      await logoService?.saveLogoSettings({
        logoTextUrl: null,
        logoTextPath: null,
        updatedBy: userProfile?.id,
      });
      setTextPreview(null);
      await loadSettings();
      showMessage('Logo text reset to default.');
    } catch (err) {
      showMessage(err?.message || 'Reset failed', 'error');
    } finally {
      setTextUploading(false);
    }
  };

  const currentIconSrc = iconPreview || settings?.logo_icon_url || DEFAULT_ICON;
  const currentTextSrc = textPreview || settings?.logo_text_url || DEFAULT_TEXT;

  return (
    <div className="mt-8 space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-foreground">Logo & Branding</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Upload a custom logo icon and logo text. Changes are reflected site-wide immediately.
        </p>
      </div>
      {/* Status message */}
      {message && (
        <div className={`px-4 py-3 rounded-lg text-sm font-medium ${
          message?.type === 'error' ?'bg-destructive/10 text-destructive border border-destructive/20' :'bg-green-500/10 text-green-400 border border-green-500/20'
        }`}>
          {message?.text}
        </div>
      )}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[0, 1]?.map((i) => (
            <div key={i} className="bg-card border border-border rounded-xl p-6 animate-pulse">
              <div className="h-4 bg-muted rounded w-32 mb-4"></div>
              <div className="h-32 bg-muted rounded-lg mb-4"></div>
              <div className="h-10 bg-muted rounded"></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Logo Icon Card */}
          <div className="bg-card border border-border rounded-xl p-6 space-y-4">
            <div>
              <h3 className="text-base font-semibold text-foreground">Logo Icon</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Square icon used in the header and hero section. Recommended: PNG, 500×500px.
              </p>
            </div>

            {/* Preview */}
            <div className="flex items-center justify-center bg-muted/30 rounded-lg p-6 min-h-[140px]">
              <img
                src={currentIconSrc}
                alt="Logo Icon Preview"
                className="max-h-24 max-w-[120px] object-contain"
              />
            </div>

            {/* Source badge */}
            <div className="flex items-center gap-2">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                settings?.logo_icon_url
                  ? 'bg-primary/10 text-primary' :'bg-muted text-muted-foreground'
              }`}>
                {settings?.logo_icon_url ? '✓ Custom uploaded' : 'Using default'}
              </span>
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-2">
              <input
                ref={iconInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={handleIconUpload}
              />
              <button
                onClick={() => iconInputRef?.current?.click()}
                disabled={iconUploading}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {iconUploading ? (
                  <>
                    <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin"></span>
                    Uploading...
                  </>
                ) : (
                  <>
                    <span>⬆</span>
                    Upload Logo Icon
                  </>
                )}
              </button>
              {settings?.logo_icon_url && (
                <button
                  onClick={handleResetIcon}
                  disabled={iconUploading}
                  className="w-full px-4 py-2 text-sm text-muted-foreground border border-border rounded-lg hover:bg-muted/50 transition-colors disabled:opacity-50"
                >
                  Reset to Default
                </button>
              )}
            </div>
          </div>

          {/* Logo Text Card */}
          <div className="bg-card border border-border rounded-xl p-6 space-y-4">
            <div>
              <h3 className="text-base font-semibold text-foreground">Logo Text</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Text/wordmark shown beside the icon in the header. Recommended: PNG, transparent background.
              </p>
            </div>

            {/* Preview */}
            <div className="flex items-center justify-center bg-muted/30 rounded-lg p-6 min-h-[140px]">
              <img
                src={currentTextSrc}
                alt="Logo Text Preview"
                className="max-h-16 max-w-[220px] object-contain"
              />
            </div>

            {/* Source badge */}
            <div className="flex items-center gap-2">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                settings?.logo_text_url
                  ? 'bg-primary/10 text-primary' :'bg-muted text-muted-foreground'
              }`}>
                {settings?.logo_text_url ? '✓ Custom uploaded' : 'Using default'}
              </span>
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-2">
              <input
                ref={textInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={handleTextUpload}
              />
              <button
                onClick={() => textInputRef?.current?.click()}
                disabled={textUploading}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {textUploading ? (
                  <>
                    <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin"></span>
                    Uploading...
                  </>
                ) : (
                  <>
                    <span>⬆</span>
                    Upload Logo Text
                  </>
                )}
              </button>
              {settings?.logo_text_url && (
                <button
                  onClick={handleResetText}
                  disabled={textUploading}
                  className="w-full px-4 py-2 text-sm text-muted-foreground border border-border rounded-lg hover:bg-muted/50 transition-colors disabled:opacity-50"
                >
                  Reset to Default
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Live preview strip */}
      <div className="bg-card border border-border rounded-xl p-4">
        <p className="text-xs text-muted-foreground mb-3 font-medium uppercase tracking-wide">Header Preview</p>
        <div className="flex items-center space-x-3 bg-background/50 rounded-lg px-4 py-3 w-fit">
          <img
            src={currentIconSrc}
            alt="Icon"
            className="w-9 h-9 object-contain"
          />
          <img
            src={currentTextSrc}
            alt="Text"
            className="h-7 object-contain"
          />
        </div>
      </div>
    </div>
  );
};

export default LogoBrandingManagement;
